/*
    Server Side Events communication channel
    serve EventSource communication with automatic reconnection on errors
    The reconnection interval is increased (doubled) on each failed attempt in order to prevent server and channel overloading
 */

import {clientConxtextID, elog} from "@/engine/helpers/logging.js";

export class SSE {
    //create SSE (EventSource) connection and register SSE messages callback function (handlers)
    //uri - uri to connect to
    //handlers - callbacks struct, where key - message type, value - callback
    //name - just for logging purposes

    constructor(uri, handlers, name) {
        this.uri = uri;
        this.name = name ? name : uri;
        this.handlers = handlers;
        this.reconnectionAttempt = 0;
        this.establishedTimestamp = 0;
        this.maxReconnectionInterval = 60; //seconds
        this.es = null;
        //logger (can be redefined with global elog function(severity, message)
        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };
        this.elog("DEBUG", `Creating SSE connection '${this.name}'`);
        this.connect();
    }

    connect() {
        if (this.es && this.es.readyState !== EventSource.CLOSED) {
            this.elog("ERROR", `SSE connection '${this.name}' has been already established`);
            return false
        }

        //eventsource doesn't support custom http headers, so we send our parameters as a query
        this.es = new EventSource(this.uri + "?client-context-id=" + clientConxtextID);
        Object.keys(this.handlers).forEach( function (item) {
            this.es.addEventListener(item, this.handlers[item])
        }.bind(this));

        // here we tried to debug firefox bug "Connection was interrupted while the page was loading."
        /*
        this.es.addEventListener("handshake", function (event){
            elog("handshake event was received")
        })
         */


        this.es.onopen = function () {
            this.elog("DEBUG", `A new SSE '${this.name}' connection has been established`);
            this.reconnectionAttempt = 0;
            this.establishedTimestamp = new Date().getTime();
        }.bind(this);

        this.es.onerror = function (err) {
            err.target.close()
            this.elog("DEBUG", `EventSource connection '${this.name}' has been interrupted, connection age: ` + Math.floor((new Date().getTime() - this.establishedTimestamp) / 1000) + "s");
            this.reconnect()
        }.bind(this);

        return true;
    }

    reconnect() {
        let wait = 1 << this.reconnectionAttempt;
        this.reconnectionAttempt++;
        wait = wait > this.maxReconnectionInterval ? this.maxReconnectionInterval : wait;
        setTimeout( function () {
            this.elog("DEBUG", `Reconnecting '${this.name}' connection, attempt: ${this.reconnectionAttempt} `);
            this.connect();
        }.bind(this), wait * 1000);
    }

    close() {
        this.es.close();
        this.elog("DEBUG", `SSE connection '${this.name}' has been closed`)
    }

    isAlive() {
        return (this.es && this.es.readyState !== 2);
    }

    eventSource() {
        return this.es
    }
}
